import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Radio, Select, Spin, Table } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { decode as decodeHtml } from 'he';

import { storageItemApi } from '../../../../../apis';
import {
  BulkCreateSpecialHandlingFeeDto,
  BulkSpecialHandlingFeeResult,
  BulkSpecialHandlingFeeResultRow,
  StorageItem,
} from '../../../../../apis/client-axios';
import CustomInput from '../../../../../components/input/CustomInput';
import { CustomCommonButton } from '../../../../../components/buttons/common/CustomButton';
import NotificationError from '../../../../../components/notifications/NotificationError';
import SvgIconDelete from '../../../../../components/@svg/SvgIconDelete';
import './index.scss';

/**
 * Task 5/6 — 手数料一括追加 (商品管理 → 一括操作).
 * Nhiều item × nhiều hạng mục phí; mỗi cặp = 1 charge PayJP riêng (BE chạy tuần tự, không dừng khi 1 cặp lỗi).
 * 3 bước trong cùng modal: edit → confirm → result.
 */

type FeeLine = {
  key: number;
  title: string;
  chargeAmount: string;
  isProfit: boolean;
};

type FeeLineError = { title: boolean; chargeAmount: boolean };

type Stage = 'edit' | 'confirm' | 'result';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Gọi khi có ít nhất 1 cặp charge thành công (để refetch danh sách). */
  onSuccess?: () => void;
};

const ERROR_JP: Record<string, string> = {
  NO_PAYMENT_METHOD: '支払い方法が登録されていません',
  CHARGE_MONEY_FAILED: '決済に失敗しました',
  STORAGE_ITEM_NOT_FOUND: '商品が見つかりません',
  TITLE_REQUIRED: '手数料名が未入力です',
  CHARGE_AMOUNT_MUST_BE_POSITIVE: '金額が無効です',
};

const errorToJp = (code?: string) => (code ? ERROR_JP[code] ?? code : '');

const newLine = (key: number): FeeLine => ({ key, title: '', chargeAmount: '', isProfit: true });

const SEARCH_PAGE_SIZE = 20;

export const BulkSpecialFeeModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const intl = useIntl();

  const [stage, setStage] = useState<Stage>('edit');
  const [selectedItems, setSelectedItems] = useState<StorageItem[]>([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fees, setFees] = useState<FeeLine[]>([newLine(1)]);
  const [feeErrors, setFeeErrors] = useState<Record<number, FeeLineError>>({});
  const [result, setResult] = useState<BulkSpecialHandlingFeeResult | null>(null);
  const [nextKey, setNextKey] = useState(2);

  // Reset toàn bộ khi mở lại
  useEffect(() => {
    if (isOpen) {
      setStage('edit');
      setSelectedItems([]);
      setSearchText('');
      setDebouncedSearch('');
      setFees([newLine(1)]);
      setFeeErrors({});
      setResult(null);
      setNextKey(2);
    }
  }, [isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const { data: searchData, isFetching: isSearching } = useQuery({
    queryKey: ['bulkSpecialFee', 'searchItems', debouncedSearch],
    queryFn: () =>
      storageItemApi.storageItemControllerItemManagementAdmin(
        1,
        SEARCH_PAGE_SIZE,
        null,
        debouncedSearch || undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'ALL',
      ),
    enabled: isOpen && stage === 'edit',
    staleTime: 5000,
  });
  const searchResults = useMemo<StorageItem[]>(() => (searchData?.data?.content as StorageItem[]) ?? [], [searchData]);

  const selectedIds = useMemo(() => new Set(selectedItems.map((i) => i.id)), [selectedItems]);

  const itemLabel = (item: StorageItem) =>
    `${item.internalSku ?? ''}｜${item.storageRequest?.user?.name ?? ''} ${item.storageRequest?.user?.userCode ?? ''}｜${decodeHtml(
      item.itemName || '',
    )}`;

  // Option = kết quả tìm kiếm + item đã chọn (để tag của Select vẫn có label khi đổi từ khoá)
  const selectOptions = useMemo(() => {
    const merged: StorageItem[] = [...selectedItems, ...searchResults.filter((r) => !selectedIds.has(r.id))];
    return merged.map((item) => ({ value: item.id, label: itemLabel(item) }));
  }, [selectedItems, searchResults, selectedIds]);

  const handleSelectItem = (id: number) => {
    const item = searchResults.find((r) => r.id === id);
    if (!item || selectedIds.has(id)) return;
    setSelectedItems((prev) => [...prev, item]);
  };

  const handleRemoveItem = (id: number) => setSelectedItems((prev) => prev.filter((i) => i.id !== id));

  const updateFee = (key: number, patch: Partial<FeeLine>) => {
    setFees((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
    setFeeErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev[key] };
      if (patch.title !== undefined) next.title = false;
      if (patch.chargeAmount !== undefined) next.chargeAmount = false;
      return { ...prev, [key]: next };
    });
  };

  const addFeeLine = () => {
    setFees((prev) => [...prev, newLine(nextKey)]);
    setNextKey((k) => k + 1);
  };

  const removeFeeLine = (key: number) => {
    setFees((prev) => (prev.length <= 1 ? prev : prev.filter((f) => f.key !== key)));
    setFeeErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const feeTotal = useMemo(
    () => fees.reduce((sum, f) => sum + (Number.isFinite(Number(f.chargeAmount)) ? Number(f.chargeAmount) : 0), 0),
    [fees],
  );
  const itemCount = selectedItems.length;
  const feeCount = fees.length;
  const pairCount = itemCount * feeCount;
  const grandTotal = itemCount * feeTotal;

  const validateFees = () => {
    const errors: Record<number, FeeLineError> = {};
    let ok = true;
    for (const f of fees) {
      const amount = Number(f.chargeAmount.trim());
      const e: FeeLineError = {
        title: !f.title.trim(),
        chargeAmount: !f.chargeAmount.trim() || !Number.isFinite(amount) || amount <= 0,
      };
      if (e.title || e.chargeAmount) {
        ok = false;
        errors[f.key] = e;
      }
    }
    setFeeErrors(errors);
    return ok;
  };

  const handleGoConfirm = () => {
    if (!itemCount) {
      NotificationError({ content: '商品を選択してください。' });
      return;
    }
    if (!validateFees()) return;
    setStage('confirm');
  };

  const bulkMutation = useMutation({
    mutationFn: (dto: BulkCreateSpecialHandlingFeeDto) =>
      storageItemApi.storageItemControllerAddSpecialHandlingFeeBulk(dto),
    onSuccess: ({ data }) => {
      setResult(data);
      setStage('result');
      if ((data?.succeeded ?? 0) > 0) onSuccess?.();
    },
    onError: () => {
      NotificationError({ content: '登録に失敗しました。' });
      setStage('edit');
    },
  });

  const handleSubmit = () => {
    if (bulkMutation.isPending) return;
    bulkMutation.mutate({
      storageItemIds: selectedItems.map((i) => i.id),
      fees: fees.map((f) => ({
        title: f.title.trim(),
        chargeAmount: Number(f.chargeAmount.trim()),
        isProfit: f.isProfit,
      })),
    });
  };

  const handleClose = () => {
    if (bulkMutation.isPending) return;
    onClose();
  };

  // ───────────────────────── columns ─────────────────────────
  const selectedItemColumns = [
    {
      title: 'BayPack管理番号',
      dataIndex: 'internalSku',
      key: 'internalSku',
      width: 180,
      render: (v: string) => <span className="bulk-special-fee__cell">{v}</span>,
    },
    {
      title: intl.formatMessage({ id: 'item.customer' }),
      key: 'customer',
      width: 160,
      render: (_: unknown, record: StorageItem) => (
        <span className="bulk-special-fee__cell">
          {record.storageRequest?.user?.name}
          <br />
          <span className="bulk-special-fee__sub">{record.storageRequest?.user?.userCode}</span>
        </span>
      ),
    },
    {
      title: intl.formatMessage({ id: 'item.productName' }),
      dataIndex: 'itemName',
      key: 'itemName',
      render: (v: string) => <span className="bulk-special-fee__cell text-2-line">{decodeHtml(v || '')}</span>,
    },
    {
      title: '',
      key: 'remove',
      width: 56,
      render: (_: unknown, record: StorageItem) => (
        <button type="button" className="bulk-special-fee__icon-btn" onClick={() => handleRemoveItem(record.id)}>
          <SvgIconDelete />
        </button>
      ),
    },
  ];

  const feeColumns = [
    {
      title: (
        <span>
          手数料名<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'title',
      render: (_: unknown, line: FeeLine) => (
        <div className="bulk-special-fee__field">
          <CustomInput
            key={`title-${line.key}`}
            placeholder="例: 梱包手数料"
            height={40}
            status={feeErrors[line.key]?.title ? 'error' : undefined}
            value={line.title}
            onChange={(e) => updateFee(line.key, { title: e.target.value })}
          />
          {feeErrors[line.key]?.title && <span className="bulk-special-fee__error">手数料名を入力してください。</span>}
        </div>
      ),
    },
    {
      title: (
        <span>
          金額 ($)<span className="bulk-special-fee__required">*</span>
        </span>
      ),
      key: 'chargeAmount',
      width: 180,
      render: (_: unknown, line: FeeLine) => (
        <div className="bulk-special-fee__field">
          <CustomInput
            key={`amount-${line.key}`}
            type="number"
            placeholder="0.00"
            height={40}
            min={0}
            status={feeErrors[line.key]?.chargeAmount ? 'error' : undefined}
            value={line.chargeAmount}
            onKeyDown={(e) => (['e', 'E', '+', '-'].includes(e.key) ? e.preventDefault() : null)}
            onChange={(e) => updateFee(line.key, { chargeAmount: e.target.value })}
          />
          {feeErrors[line.key]?.chargeAmount && (
            <span className="bulk-special-fee__error">金額を入力してください。</span>
          )}
        </div>
      ),
    },
    {
      title: '粗利区分',
      key: 'isProfit',
      width: 230,
      render: (_: unknown, line: FeeLine) => (
        <Radio.Group value={line.isProfit} onChange={(e) => updateFee(line.key, { isProfit: e.target.value })}>
          <Radio value={true}>粗利対象</Radio>
          <Radio value={false}>粗利対象外</Radio>
        </Radio.Group>
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 56,
      render: (_: unknown, line: FeeLine) => (
        <button
          type="button"
          className="bulk-special-fee__icon-btn"
          disabled={fees.length <= 1}
          onClick={() => removeFeeLine(line.key)}
        >
          <SvgIconDelete />
        </button>
      ),
    },
  ];

  const resultColumns = [
    {
      title: '結果',
      key: 'success',
      width: 80,
      render: (_: unknown, r: BulkSpecialHandlingFeeResultRow) => (
        <span className={`bulk-special-fee__result-tag ${r.success ? 'is-ok' : 'is-ng'}`}>
          {r.success ? '成功' : '失敗'}
        </span>
      ),
    },
    { title: 'BayPack管理番号', dataIndex: 'internalSku', key: 'internalSku', width: 180 },
    {
      title: intl.formatMessage({ id: 'item.productName' }),
      dataIndex: 'itemName',
      key: 'itemName',
      render: (v: string) => <span className="text-2-line">{decodeHtml(v || '')}</span>,
    },
    { title: '手数料名', dataIndex: 'title', key: 'title', width: 160 },
    {
      title: '金額',
      dataIndex: 'chargeAmount',
      key: 'chargeAmount',
      width: 100,
      render: (v: number) => `$${Number(v ?? 0).toFixed(2)}`,
    },
    {
      title: '粗利区分',
      dataIndex: 'isProfit',
      key: 'isProfit',
      width: 100,
      render: (v: boolean) => (v ? '粗利対象' : '粗利対象外'),
    },
    {
      title: 'エラー',
      dataIndex: 'error',
      key: 'error',
      width: 200,
      render: (v: string) => <span className="bulk-special-fee__error-text">{errorToJp(v)}</span>,
    },
  ];

  const summaryText = `${itemCount}商品 × ${feeCount}項目 = ${pairCount}件　合計 $${grandTotal.toFixed(2)}`;

  // ───────────────────────── render ─────────────────────────
  return (
    <Modal
      title=""
      closable={false}
      maskClosable={false}
      open={isOpen}
      onCancel={handleClose}
      className="bulk-special-fee-modal"
      centered
      destroyOnClose
      footer={null}
      width={1040}
    >
      {stage === 'edit' && (
        <>
          <p className="bulk-special-fee__title">手数料一括追加</p>

          <div className="bulk-special-fee__section">
            <div className="bulk-special-fee__section-header">
              <span>
                対象商品<span className="bulk-special-fee__required">*</span>
              </span>
              <span className="bulk-special-fee__count">選択中: {itemCount}件</span>
            </div>
            <Select
              className="bulk-special-fee__select"
              mode="multiple"
              showSearch
              filterOption={false}
              placeholder="SKU、追跡番号、商品名、バイヤー名を検索して商品を追加"
              value={selectedItems.map((i) => i.id)}
              options={selectOptions}
              searchValue={searchText}
              onSearch={setSearchText}
              onSelect={(id) => handleSelectItem(Number(id))}
              onDeselect={(id) => handleRemoveItem(Number(id))}
              notFoundContent={isSearching ? <Spin size="small" /> : '該当する商品がありません'}
              maxTagCount={0}
              maxTagPlaceholder={() => null}
              popupClassName="bulk-special-fee__dropdown"
            />
            <Table
              className="bulk-special-fee__table"
              size="small"
              rowKey="id"
              pagination={false}
              columns={selectedItemColumns as any}
              dataSource={selectedItems}
              locale={{ emptyText: '商品が選択されていません' }}
              scroll={{ y: 220 }}
            />
          </div>

          <div className="bulk-special-fee__section">
            <div className="bulk-special-fee__section-header">
              <span>手数料項目</span>
              <button type="button" className="bulk-special-fee__add-line" onClick={addFeeLine}>
                ＋ 行追加
              </button>
            </div>
            <Table
              className="bulk-special-fee__table bulk-special-fee__table--fees"
              size="small"
              rowKey="key"
              pagination={false}
              columns={feeColumns as any}
              dataSource={fees}
            />
          </div>

          <div className="bulk-special-fee__summary">{summaryText}</div>

          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{ className: 'button-small', onClick: handleClose }}
              content={intl.formatMessage({ id: 'common.cancel' })}
            />
            <CustomCommonButton
              buttonProps={{
                className: 'button-small',
                type: 'primary',
                onClick: handleGoConfirm,
                disabled: !itemCount || !feeCount,
              }}
              content={intl.formatMessage({ id: 'common.add' })}
            />
          </div>
        </>
      )}

      {stage === 'confirm' && (
        <>
          <p className="bulk-special-fee__title">手数料一括追加の確認</p>
          <div className="bulk-special-fee__confirm">
            <p className="bulk-special-fee__confirm-main">{summaryText}</p>
            <p className="bulk-special-fee__confirm-note">
              各商品 × 各項目ごとに即時決済を行います（{pairCount}件の決済）。
              <br />
              決済後の取り消しはできません。よろしいですか？
            </p>
            <ul className="bulk-special-fee__confirm-list">
              {fees.map((f) => (
                <li key={f.key}>
                  {f.title.trim()}：${Number(f.chargeAmount).toFixed(2)}（{f.isProfit ? '粗利対象' : '粗利対象外'}）
                </li>
              ))}
            </ul>
          </div>
          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              isCancelButton={true}
              buttonProps={{
                className: 'button-small',
                onClick: () => setStage('edit'),
                disabled: bulkMutation.isPending,
              }}
              content="戻る"
            />
            <CustomCommonButton
              buttonProps={{
                className: 'button-small',
                type: 'primary',
                onClick: handleSubmit,
                disabled: bulkMutation.isPending,
                loading: bulkMutation.isPending,
              }}
              content="請求する"
            />
          </div>
        </>
      )}

      {stage === 'result' && result && (
        <>
          <p className="bulk-special-fee__title">手数料一括追加の結果</p>
          <div className="bulk-special-fee__result-summary">
            <span>合計 {result.total}件</span>
            <span className="is-ok">成功 {result.succeeded}件</span>
            <span className={result.failed > 0 ? 'is-ng' : ''}>失敗 {result.failed}件</span>
          </div>
          {result.failed > 0 && (
            <p className="bulk-special-fee__result-note">
              失敗した項目は決済されていません。原因を確認のうえ、該当商品で再度登録してください。
            </p>
          )}
          <Table
            className="bulk-special-fee__table"
            size="small"
            rowKey={(r: BulkSpecialHandlingFeeResultRow, idx) => `${r.storageItemId}-${idx}`}
            pagination={false}
            columns={resultColumns as any}
            dataSource={result.results}
            scroll={{ y: 360 }}
          />
          <div className="bulk-special-fee__footer">
            <CustomCommonButton
              buttonProps={{ className: 'button-small', type: 'primary', onClick: onClose }}
              content="閉じる"
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default BulkSpecialFeeModal;
